<template>
    <v-dialog
        v-model="modelValue"
        eager
        fullscreen
        transition="dialog-top-transition">
        <template #activator="{ props: activatorProps }">
            <v-btn
                class="tw-relative tw-border-gray-300 tw-text-dark"
                height="35"
                icon
                variant="outlined"
                width="35"
                v-bind="activatorProps">
                <NotificationIcon class="tw-h-4 tw-w-4" />
                <span
                    v-show="hasNotifications"
                    class="tw-absolute tw-right-[9px] tw-top-[9px] tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary"></span>
            </v-btn>
        </template>

        <v-card color="white">
            <v-toolbar
                class="-tw-mb-3 tw-px-3"
                color="white">
                <v-toolbar-title class="tw-grow-[2] tw-font-title tw-text-lg tw-font-medium">{{ $t('pages.notifications.title') }}</v-toolbar-title>

                <v-spacer></v-spacer>

                <v-btn
                    class="tw-text-dark"
                    elevation="0"
                    height="36"
                    icon="mdi-close"
                    variant="text"
                    @click="modelValue = false"></v-btn>
            </v-toolbar>
            <div
                v-if="notifications.length"
                class="tw-flex tw-flex-row tw-items-center tw-justify-between tw-px-3">
                <v-progress-circular
                    v-if="isLoading"
                    class="tw-ml-3"
                    color="primary"
                    indeterminate
                    size="16"
                    width="1"></v-progress-circular>
                <v-btn
                    class="tw-ml-auto"
                    color="primary"
                    variant="text"
                    @click="clearAllNotifications">
                    <v-icon class="tw-mr-1 tw-size-[14px] sm:tw-size-[16px]">mdi-delete</v-icon>
                    <span class="tw-text-xs tw-font-medium tw-normal-case tw-tracking-wide sm:tw-text-sm">{{ $t('Notifications.markRead') }}</span>
                </v-btn>
            </div>
            <v-list
                v-if="notifications.length"
                id="notifications-list"
                class="tw-bg-white tw-pt-0">
                <template
                    v-for="notification in notifications"
                    :key="notification.id">
                    <div class="notification-swipe-container tw-relative tw-overflow-hidden">
                        <!-- Swipe Action Background -->
                        <div class="notification-action-bg tw-pointer-events-none tw-absolute tw-inset-0 tw-z-0 tw-flex tw-items-center tw-justify-between tw-px-6">
                            <div
                                class="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-inset-y-1 tw-z-0 tw-flex tw-items-center tw-gap-2 tw-bg-red tw-px-6 tw-text-white tw-opacity-100"
                                :class="{
                                    'tw-justify-start': notificationPositions[notification.id ?? 0] > 10,
                                    'tw-justify-end': notificationPositions[notification.id ?? 0] < -10,
                                }">
                                <v-icon
                                    icon="mdi-delete"
                                    size="24" />
                                <span class="tw-text-xs tw-font-light">Eliminar</span>
                            </div>
                        </div>
                        <PostListItem
                            ref="notificationEl"
                            class="tw-px-6"
                            :data-notification-id="notification.id"
                            :post="notification.post"
                            :style="{
                                transform: notificationPositions[notification.id ?? 0] ? `translateX(${notificationPositions[notification.id ?? 0]}px)` : '',
                            }"></PostListItem>
                    </div>
                    <v-divider
                        class="tw-mx-6 tw-my-4"
                        opacity="0.1"></v-divider>
                </template>
            </v-list>
            <div
                v-else
                class="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-8">
                <template v-if="isLoading">
                    <v-progress-circular
                        class="tw-mb-4"
                        color="primary"
                        indeterminate
                        size="24"
                        width="2"></v-progress-circular>
                </template>
                <template v-else-if="notificationsStore.getSubscribedCategories.length > 0">
                    <BellFilledIcon class="tw-h-24" />
                    <span class="tw-max-w-48 tw-text-center tw-text-gray-400">{{ $t('Notifications.nothingToRead') }}</span>
                </template>
                <template v-else>
                    <BellFilledIcon class="tw-h-24" />
                    <span class="tw-max-w-48 tw-whitespace-pre-line tw-text-center tw-text-gray-400">{{ $t('Notifications.noSubscriptions') }}</span>
                    <Button
                        :to="{
                            name: 'Notifications',
                        }">
                        {{ $t('Notifications.subscribe') }}
                    </Button>
                </template>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
    import NotificationIcon from '@/components/icons/Notification.vue';
    import BellFilledIcon from '@/components/icons/BellFilled.vue';
    import _ from 'lodash';
    import Hammer from 'hammerjs';
    import { useMounted } from '@vueuse/core';
    import type { VListItem } from 'vuetify/components';
    import type { INotification, WPPost } from '@/contentTypes';
    import { useNotificationsStore } from '@/store/notifications';
    import { deleteNotifications, fetchNotifications } from '@/api/posts';

    const modelValue = defineModel<boolean>({ type: Boolean, default: false });
    const notificationsStore = useNotificationsStore();
    const hasMounted = useMounted();

    const isLoading = ref(true);
    const page = ref(1);
    const perPage = ref(20);
    const total = ref(0);
    const _notifications = computed(() => notificationsStore.getNotifications);
    const notifications = computed(() => {
        return _notifications.value.map((notification: INotification) => {
            // Convert notification to post format
            return {
                ...notification,
                post: notificationToPost(notification.notification),
            };
        });
    });

    const notificationEl = ref<VListItem[]>([]);
    const notificationPositions = ref<Record<number, number>>({});

    const hasNotifications = computed(() => {
        return !!notifications.value.length;
    });

    function clearAllNotifications() {
        const notificationIds = _notifications.value.map((notification) => notification.id);
        deleteNotifications(notificationsStore.getNotificationsToken, notificationIds);
        notificationsStore.resetNotifications();
        notificationPositions.value = {};
        page.value = 1;
        total.value = 0;
        modelValue.value = false;
    }

    function clearNotification(notificationId: number) {
        deleteNotifications(notificationsStore.getNotificationsToken, [notificationId]);
        notificationsStore.deleteNotification(notificationId);
    }

    function notificationToPost(notification: INotification['notification']): Partial<WPPost> {
        let categories: number[] = [];
        if (notification.categories && typeof notification.categories == 'string') {
            try {
                categories = JSON.parse(notification.categories);
            } catch (error) {
                categories = [];
            }
        } else if (Array.isArray(notification.categories)) {
            categories = notification.categories;
        }
        const _post: Partial<WPPost> = {
            id: notification.id,
            wpId: notification.id,
            title: notification.title,
            date: notification.date,
            publishedAt: notification.date,
            featuredImageUrl: notification.img,
            categories,
            categoryWpIds: categories,
        };
        return _post;
    }

    async function getNotifications() {
        if (notificationsStore.getNotificationsToken) {
            try {
                const { data } = await fetchNotifications({
                    deviceToken: notificationsStore.getNotificationsToken,
                    page: page.value,
                    perPage: perPage.value,
                });
                total.value = data.total || 0;
                if (page.value === 1) {
                    notificationsStore.setNotifications(data.notifications || []);
                } else {
                    const currentNotifications = notificationsStore.getNotifications;
                    notificationsStore.setNotifications([...currentNotifications, ...(data.notifications || [])]);
                }
            } catch (error) {
                console.error('Error fetching notifications:', error);
                // Handle error appropriately, e.g., show a toast or alert
            } finally {
                isLoading.value = false;
            }
        } else {
            isLoading.value = false;
        }
    }

    // Add swipe gesture detection
    watchPostEffect(async () => {
        if (hasMounted.value && modelValue.value) {
            await getNotifications();
            await nextTick();

            notificationEl.value.forEach((el) => {
                // Check if the element already has a swipe event listener
                if (el.$el.getAttribute('data-swipe-enabled') === 'true') {
                    return;
                }
                const hammertime = new Hammer(el.$el);
                let startX = 0;
                const notificationId = Number(el.$el.getAttribute('data-notification-id'));

                hammertime.on('panstart', () => {
                    startX = notificationPositions.value[notificationId] || 0;
                    el.$el.style.transition = '';
                });

                hammertime.on('panmove', (ev) => {
                    notificationPositions.value = {
                        ...notificationPositions.value,
                        [notificationId]: startX + ev.deltaX,
                    };
                });

                hammertime.on('panend', (ev) => {
                    const screenWidth = window.innerWidth;
                    const threshold = screenWidth * 0.25; // 25% of screen width

                    if (Math.abs(ev.deltaX) > threshold) {
                        // Animate out
                        el.$el.style.transition = 'transform 0.2s';
                        notificationPositions.value = {
                            ...notificationPositions.value,
                            [notificationId]: ev.deltaX > 0 ? 500 : -500,
                        };
                        setTimeout(() => {
                            clearNotification(notificationId);
                            // Clean up position
                            notificationPositions.value = {
                                ...notificationPositions.value,
                                [notificationId]: 0,
                            };
                        }, 200);
                    } else {
                        // Snap back
                        el.$el.style.transition = 'transform 0.2s';
                        notificationPositions.value = {
                            ...notificationPositions.value,
                            [notificationId]: 0,
                        };
                    }
                });

                // add a data- attribute to indicate that the element swipe is enabled
                el.$el.setAttribute('data-swipe-enabled', 'true');
            });
        }
    });

    window.onfocus = () => {
        getNotifications();
    };

    getNotifications();
</script>

<style lang="scss">
    .notification-swipe-container {
        .notification-action-bg {
            background: #fff;
            height: 100%;
            width: 100%;
            pointer-events: none;
            user-select: none;
        }
    }
</style>
